/**
 * message-history-api — wires Messaging → Delivery to the typed notification
 * history ops (wiring kit `…-notification-history-9bf1703c`):
 *
 *   listMessageHistory       GET /v1/admin/message-history       (table)
 *   getMessageHistoryDetail  GET /v1/admin/message-history/{id}   (drawer)
 *
 * Every call goes through the typed @v0y4ge/api-client (never a hand-built
 * fetch). Results are `tag`-reported for the data-source transparency strip:
 * a `__source: "cd-preview-seed"` marker (preview seed layer) surfaces the op
 * on <DataSourceStrip> until the real api-mocks / backend serve it.
 *
 * Adapters map the contract shapes (MessageHistoryItem / MessageHistoryDetail)
 * onto the existing DeliveryRow the table + drawer already render, so the UI is
 * unchanged — only the data source moved from static seeds to the ops.
 */
import { listMessageHistory, getMessageHistoryDetail } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";
import type { DeliveryRow, DelivEvent } from "./delivery-data";

/* Contract shapes (mirrors @v0y4ge/api-contracts; kept local so the screen
 * doesn't depend on contract type resolution — types are erased at build). */
export interface MHItem {
  id: string; display_id: string; channel: string; status: string;
  provider: string; credential_source: string;
  subject: string | null; to_address: string; to_name?: string | null;
  from_name?: string | null; from_address: string;
  source_kind?: string | null; source_label?: string | null;
  created_at: string; last_event_at?: string | null;
}
export interface MHEvent {
  type: string; occurred_at: string; client_label?: string | null;
  bounce_classification?: string | null; bounce_reason?: string | null;
}
export interface MHDetail extends MHItem {
  reply_to?: string | null; events: MHEvent[];
  rendered_html?: string | null; rendered_text?: string | null; body_url?: string | null;
}
export interface MHListResponse { data: MHItem[]; total: number; page: number; limit: number; hasMore: boolean; }
export interface MHListParams {
  channel?: string; status?: string; recipient?: string; source_kind?: string;
  from_date?: string; to_date?: string; page?: number; limit?: number;
}

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

export function fetchMessageHistory(params: MHListParams = {}): Promise<MHListResponse> {
  return tag("listMessageHistory", listMessageHistory(params as never) as Promise<MHListResponse>);
}
export function fetchMessageHistoryDetail(id: string): Promise<MHDetail> {
  return tag("getMessageHistoryDetail", getMessageHistoryDetail(id) as Promise<MHDetail>);
}

/* ── Shape mapping ─────────────────────────────────────────────────────── */
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** Format an ISO instant as "13 Jul, 10:00:04" (UTC) — matches delivTimeVal's parser so sort works. */
function fmtAt(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCDate()} ${MON[d.getUTCMonth()]}, ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`;
}

/** Contract source_kind → the delivery log's source vocabulary. */
function mapSourceKind(kind?: string | null): string {
  switch (kind) {
    case "journey": return "flow";
    case "trigger": return "trigger";
    case "campaign": return "campaign";
    case "manual": return "manual";
    default: return "manual";
  }
}

function sender(item: MHItem): { name: string; from: string } {
  return { name: item.from_name || "System", from: item.from_address };
}
function recipient(item: MHItem): { name: string; to: string } {
  return { name: item.to_name || item.to_address, to: item.to_address };
}
function source(item: MHItem): { kind: string; label: string } {
  return { kind: mapSourceKind(item.source_kind), label: item.source_label || "System notification" };
}

/** A list row — no timeline/body yet (that's the detail fetch on open). */
export function itemToRow(item: MHItem): DeliveryRow {
  const isEmail = item.channel === "email";
  return {
    id: item.id,
    channel: item.channel,
    sender: sender(item),
    recipient: recipient(item),
    source: source(item),
    subject: isEmail ? (item.subject ?? undefined) : undefined,
    text: isEmail ? undefined : (item.source_label ?? "SMS notification"),
    sent: fmtAt(item.created_at),
    // Single projected-status event drives the row's pill + "latest event" sort.
    events: [{ type: item.status, at: fmtAt(item.last_event_at ?? item.created_at) }],
    displayId: item.display_id,
    status: item.status,
    provider: item.provider,
    credentialSource: item.credential_source,
  };
}

/** The full drawer row — real event timeline + rendered body. */
export function detailToRow(d: MHDetail): DeliveryRow {
  const isEmail = d.channel === "email";
  const events: DelivEvent[] = (d.events || []).map((e) => {
    const meta = e.bounce_reason || e.client_label || undefined;
    return meta ? { type: e.type, at: fmtAt(e.occurred_at), meta } : { type: e.type, at: fmtAt(e.occurred_at) };
  });
  // Keep the projected-status pill available even when the timeline is empty.
  if (events.length === 0) events.push({ type: d.status, at: fmtAt(d.last_event_at ?? d.created_at) });
  const html = d.rendered_html ?? undefined;
  const text = d.rendered_text ?? undefined;
  return {
    id: d.id,
    channel: d.channel,
    sender: sender(d),
    recipient: recipient(d),
    source: source(d),
    subject: d.subject ?? undefined,
    text,
    html,
    sent: fmtAt(d.created_at),
    events,
    displayId: d.display_id,
    status: d.status,
    provider: d.provider,
    credentialSource: d.credential_source,
    replyTo: d.reply_to ?? undefined,
    bodyUnavailable: isEmail ? !html : !text,
  };
}
